import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  CircularProgress,
  Grid,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import privilegeService, {
  getRolePrivileges,
  saveRolePrivileges,
  saveSubmenuPermissions,
  getSubmenuPermissions
} from '../services/privilegeService';
import axiosInstance from '../utils/axios';

// Menu structure with submenus - MUST match Sidebar.js exactly
const menuStructure = [
  {
    id: 'DASHBOARD',
    text: 'Dashboard',
    path: '/dashboard',
    submenus: []
  },
  {
    id: 'INVENTORY',
    text: 'Inventory',
    path: '/inventory',
    submenus: [
      { id: 'INVENTORY_CATEGORIES', text: 'Categories', path: '/inventory/categories' },
      { id: 'INVENTORY_PRODUCTS', text: 'Products', path: '/inventory/products' },
      { id: 'INVENTORY_IMPORT_PRODUCTS', text: 'Import Products', path: '/inventory/import-products' },
      { id: 'INVENTORY_BARCODE_GENERATE', text: 'Barcode / QR Generate', path: '/inventory/barcode-generate' },
      { id: 'INVENTORY_TAXES', text: 'Taxes', path: '/inventory/taxes' },
      { id: 'INVENTORY_UNITS', text: 'Units', path: '/inventory/units' },
      { id: 'INVENTORY_STOCK_TRANSFER', text: 'Stock Transfer', path: '/inventory/stock-transfer' },
      { id: 'INVENTORY_STOCK_TRANSFER_CREATE', text: 'Create Stock Transfer', path: '/inventory/stock-transfer/create' },
      { id: 'INVENTORY_PHYSICAL_STOCK_UPDATE', text: 'Physical Stock Update', path: '/inventory/physical-stock-update' }
    ]
  },
  {
    id: 'PHYSICAL_STOCK_MANAGEMENT',
    text: 'Physical Stock Management',
    path: '/inventory/physical-stock-entry',
    submenus: [
      { id: 'PHYSICAL_STOCK_ENTRY', text: 'Stock Entry', path: '/inventory/physical-stock-entry' },
      { id: 'PHYSICAL_STOCK_VIEW', text: 'View Stock Entry', path: '/inventory/view-physical-stock-entry' }
    ]
  },
  {
    id: 'EXPENSES',
    text: 'Expense Management',
    path: '/expenses',
    submenus: [
      { id: 'EXPENSES_ENTRY', text: 'Expense Entry', path: '/expenses/entry' }
    ]
  },
  {
    id: 'LOCATION_MANAGEMENT',
    text: 'Location Management',
    path: '/location-management',
    submenus: [
      { id: 'LOCATION_MANAGEMENT_LOCATIONS', text: 'Locations', path: '/inventory/locations' },
      { id: 'LOCATION_MANAGEMENT_USERS', text: 'Location Users', path: '/inventory/location-users' }
    ]
  },
  {
    id: 'SALES',
    text: 'Sales',
    path: '/sales',
    submenus: [
      { id: 'SALES_ORDERS', text: 'Add Order', path: '/sales/pos' },
      { id: 'SALES_POS_KEYBOARD', text: 'Sales', path: '/sales/pos-keyboard' },
      { id: 'SALES_VIEW_ORDERS', text: 'View Orders', path: '/sales/view-orders' },
      { id: 'SALES_RETURNS', text: 'Returns', path: '/sales/returns' },
      { id: 'SALES_VIEW_RETURNS', text: 'View Returns', path: '/sales/view-returns' }
    ]
  },
  {
    id: 'PURCHASES',
    text: 'Purchases',
    path: '/purchases',
    submenus: [
      { id: 'PURCHASES_ORDERS', text: 'Orders', path: '/purchases/orders' },
      { id: 'PURCHASES_PO_KEYBOARD', text: 'Purchase (Keyboard)', path: '/purchases/po-keyboard' },
      { id: 'PURCHASES_VIEW_ORDERS', text: 'View Orders', path: '/purchases/view-orders' },
      { id: 'PURCHASES_RETURNS', text: 'Returns', path: '/purchases/returns' }
    ]
  },
  {
    id: 'PEOPLE',
    text: 'People',
    path: '/people',
    submenus: [
      { id: 'PEOPLE_CUSTOMERS', text: 'Customers', path: '/people/customers' },
      { id: 'PEOPLE_SUPPLIERS', text: 'Suppliers', path: '/people/suppliers' },
      { id: 'PEOPLE_USERS', text: 'Users', path: '/people/users' }
    ]
  },
  {
    id: 'PRIVILEGES',
    text: 'Privileges',
    path: '/privileges',
    submenus: []
  },
  {
    id: 'REPORTS',
    text: 'Reports',
    path: '/reports',
    submenus: [
      { id: 'REPORTS_LOCATION_SALES', text: 'Location & Warehouse Sales Reports', path: '/reports/location-sales' },
      { id: 'REPORTS_TOP_SELLING_PRODUCTS', text: 'Top Selling Products', path: '/reports/top-selling-products' },
      { id: 'REPORTS_PURCHASES', text: 'Purchase Reports', path: '/reports/purchases' },
      { id: 'REPORTS_INVENTORY', text: 'Inventory Reports', path: '/reports/inventory' },
      { id: 'REPORTS_STOCK', text: 'Stock Report', path: '/reports/stock' },
      { id: 'REPORTS_STOCK_MATRIX', text: 'Stock Matrix Report', path: '/reports/stock-matrix' },
      { id: 'REPORTS_DAY_CLOSE', text: 'Day Close Report', path: '/reports/day-close' },
      { id: 'REPORTS_STOCK_TRANSFER', text: 'Stock Transfer Report', path: '/reports/stock-transfer' },
      { id: 'REPORTS_PRODUCTS', text: 'Products Report', path: '/reports/products' },
      { id: 'REPORTS_BILLING', text: 'Billing Reports', path: '/reports/billing' },
      { id: 'REPORTS_INVOICE_PROFIT_LOSS', text: 'Invoice P&L Reports', path: '/reports/invoice-profit-loss' },
      { id: 'REPORTS_CREDIT_CUSTOMERS', text: 'Manage Credit Customers', path: '/reports/credit-customers' },
      { id: 'REPORTS_GST', text: 'GSTR1', path: '/reports/gst' },
      { id: 'REPORTS_GSTR2', text: 'GSTR2', path: '/reports/gstr2' },
      { id: 'REPORTS_GSTR3B', text: 'GSTR-3B', path: '/reports/gstr3b' },
      { id: 'REPORTS_HSN', text: 'HSN Wise Reports', path: '/reports/hsn' }
    ]
  },
  {
    id: 'SETTINGS',
    text: 'Settings',
    path: '/settings',
    submenus: [
      { id: 'SETTINGS_COMPANY', text: 'Company Settings', path: '/settings/company' },
      { id: 'SETTINGS_WEBSITE', text: 'Website Settings', path: '/settings/website' },
      { id: 'SETTINGS_PRINT', text: 'Print Settings', path: '/settings/print' },
      { id: 'SETTINGS_LABEL', text: 'Label Settings', path: '/settings/label-settings' },
      { id: 'SETTINGS_WHATSAPP', text: 'WhatsApp Configuration', path: '/settings/whatsapp' }
    ]
  }
];

const RolePrivilegesPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [roles] = useState(Object.keys(privilegeService.ROLES));
  const [selectedRole, setSelectedRole] = useState('');
  const [privileges, setPrivileges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [backendPrivileges, setBackendPrivileges] = useState(null);

  useEffect(() => {
    if (selectedRole) {
      fetchRolePrivileges(selectedRole);
    }
  }, [selectedRole]);

  const fetchRolePrivileges = async (role) => {
    try {
      setLoading(true);

      // CRITICAL: Load privileges directly from backend API to get actual database state
      // This ensures we see the real saved privileges, not cached localStorage data
      try {
        const response = await axiosInstance.get(`/role-privileges/${role}`);
        const backendData = response.data;
        setBackendPrivileges(backendData);
        console.log('Fetched backend privileges for role:', role, backendData);

        // Convert backend response to UI format
        const menuPrivileges = convertBackendToUIFormat(backendData.privileges || []);
        setPrivileges(menuPrivileges);
      } catch (error) {
        console.error('Error fetching backend privileges:', error);
        // Fallback: Initialize with empty privileges if backend fails
        const emptyPrivileges = menuStructure.map(menu => ({
          id: menu.id,
          text: menu.text,
          path: menu.path,
          canView: false,
          canCreate: false,
          canEdit: false,
          canDelete: false,
          submenus: menu.submenus.map(submenu => ({
            id: submenu.id,
            text: submenu.text,
            path: submenu.path,
            canView: false,
            canCreate: false,
            canEdit: false,
            canDelete: false
          }))
        }));
        setPrivileges(emptyPrivileges);
        enqueueSnackbar('Failed to load role privileges from backend', { variant: 'error' });
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching role privileges:', error);
      enqueueSnackbar('Failed to load role privileges', { variant: 'error' });
      setLoading(false);
    }
  };

  // Convert backend API response to UI format
  const convertBackendToUIFormat = (backendPrivileges = []) => {
    // Create maps for quick lookup
    // Map for module-level privileges: key = module name
    const modulePrivilegeMap = new Map();
    // Map for submenu-level privileges: key = submenuId
    const submenuPrivilegeMap = new Map();

    backendPrivileges.forEach(priv => {
      if (priv.submenuId == null || priv.submenuId === '') {
        // Module-level privilege
        modulePrivilegeMap.set(priv.module, priv);
      } else {
        // Submenu-level privilege
        submenuPrivilegeMap.set(priv.submenuId, priv);
      }
    });

    // Map menu structure to UI format with backend privileges
    return menuStructure.map(menu => {
      // Find module-level privilege for this menu (menu.id should match backend module name)
      const modulePriv = modulePrivilegeMap.get(menu.id);

      // Initialize menu with default values or from backend
      const menuPermission = {
        id: menu.id,
        text: menu.text,
        path: menu.path,
        canView: modulePriv?.canView || false,
        canCreate: modulePriv?.canCreate || false,
        canEdit: modulePriv?.canEdit || false,
        canDelete: modulePriv?.canDelete || false,
        submenus: []
      };

      // Map submenus with their privileges (submenu.id should match backend submenuId)
      menuPermission.submenus = menu.submenus.map(submenu => {
        const submenuPriv = submenuPrivilegeMap.get(submenu.id);

        return {
          id: submenu.id,
          text: submenu.text,
          path: submenu.path,
          canView: submenuPriv?.canView || false,
          canCreate: submenuPriv?.canCreate || false,
          canEdit: submenuPriv?.canEdit || false,
          canDelete: submenuPriv?.canDelete || false
        };
      });

      // If any submenu has view permission, ensure parent menu also has view permission
      if (menuPermission.submenus.some(submenu => submenu.canView)) {
        menuPermission.canView = true;
      }

      return menuPermission;
    });
  };

  // Convert from the service format to the UI format (legacy - kept for compatibility)
  const convertPrivilegesToUIFormat = (visibleMenus = [], accessibleRoutes = []) => {
    return menuStructure.map(menu => {
      // Main menu permissions
      const menuPermission = {
        id: menu.id,
        text: menu.text,
        path: menu.path,
        canView: visibleMenus.includes(menu.id),
        canCreate: visibleMenus.includes(menu.id),
        canEdit: visibleMenus.includes(menu.id),
        canDelete: visibleMenus.includes(menu.id),
        submenus: []
      };

      // Submenu permissions
      menuPermission.submenus = menu.submenus.map(submenu => {
        return {
          id: submenu.id,
          text: submenu.text,
          path: submenu.path,
          canView: visibleMenus.includes(submenu.id),
          canCreate: visibleMenus.includes(submenu.id),
          canEdit: visibleMenus.includes(submenu.id),
          canDelete: visibleMenus.includes(submenu.id)
        };
      });

      return menuPermission;
    });
  };

  // Convert from UI format back to service format
  const convertUIToServiceFormat = (privileges) => {
    const visibleMenus = [];
    const accessibleRoutes = [];

    privileges.forEach(menu => {
      if (menu.canView) {
        // Add main menu to visible menus
        visibleMenus.push(menu.id);

        // If the menu has a path, add it to accessible routes
        if (menu.path) {
          accessibleRoutes.push(menu.path);

          // For routes with nested paths, add wildcard permissions
          if (['SALES', 'PURCHASES', 'REPORTS', 'PEOPLE', 'INVENTORY'].includes(menu.id)) {
            accessibleRoutes.push(`${menu.path}/*`);
          }
        }

        // Process submenus
        menu.submenus.forEach(submenu => {
          if (submenu.canView) {
            // Add submenu ID to visible menus
            visibleMenus.push(submenu.id);

            // Add submenu path to accessible routes
            if (submenu.path) {
              accessibleRoutes.push(submenu.path);

              // Extract the parent path segment and add wildcard routes
              const pathParts = submenu.path.split('/').filter(p => p); // Remove empty strings
              if (pathParts.length >= 2) {
                const parentPath = `/${pathParts[0]}`; // e.g., /sales from /sales/pos

                // Add the parent path if it's not already in the list
                if (!accessibleRoutes.includes(parentPath)) {
                  accessibleRoutes.push(parentPath);
                }

                // Add wildcard for the parent path (CRITICAL for route matching)
                const wildcardPath = `${parentPath}/*`;
                if (!accessibleRoutes.includes(wildcardPath)) {
                  accessibleRoutes.push(wildcardPath);
                }
              }
            }
          }
        });
      }
    });

    // CRITICAL: Do NOT hardcode dashboard - it must come from backend privileges
    // Dashboard access should be controlled by backend, not hardcoded here

    return { visibleMenus, accessibleRoutes };
  };

  // Convert UI privileges to backend format - NOW SUPPORTS SUBMENU-LEVEL PRIVILEGES
  const convertUIToBackendFormat = (privileges) => {
    const modulePrivileges = [];

    // Map menu/submenu IDs to backend module names
    const menuToModule = {
      // Main modules
      'DASHBOARD': 'DASHBOARD',
      'INVENTORY': 'INVENTORY',
      'LOCATION_MANAGEMENT': 'INVENTORY', // Location management maps to INVENTORY module
      'SALES': 'SALES',
      'PURCHASES': 'PURCHASES',
      'PEOPLE': 'CUSTOMERS', // People main menu maps to CUSTOMERS module
      'REPORTS': 'REPORTS',
      'SETTINGS': 'SETTINGS',
      'PRIVILEGES': 'SETTINGS',

      // People submodules
      'PEOPLE_CUSTOMERS': 'CUSTOMERS',
      'PEOPLE_SUPPLIERS': 'SUPPLIERS',
      'PEOPLE_USERS': 'USERS',

      // Inventory submodules
      'INVENTORY_CATEGORIES': 'INVENTORY',
      'INVENTORY_PRODUCTS': 'INVENTORY',
      'INVENTORY_IMPORT_PRODUCTS': 'INVENTORY',
      'INVENTORY_TAXES': 'INVENTORY',
      'INVENTORY_UNITS': 'INVENTORY',
      'INVENTORY_STOCK_TRANSFER': 'INVENTORY',
      'INVENTORY_STOCK_TRANSFER_CREATE': 'INVENTORY',
      'INVENTORY_PHYSICAL_STOCK_UPDATE': 'INVENTORY',
      'LOCATION_MANAGEMENT_LOCATIONS': 'INVENTORY',
      'LOCATION_MANAGEMENT_USERS': 'INVENTORY',

      // Sales submodules
      'SALES_ORDERS': 'SALES',
      'SALES_POS_KEYBOARD': 'SALES',
      'SALES_VIEW_ORDERS': 'SALES',
      'SALES_RETURNS': 'SALES',
      'SALES_VIEW_RETURNS': 'SALES',

      // Purchases submodules
      'PURCHASES_ORDERS': 'PURCHASES',
      'PURCHASES_PO_KEYBOARD': 'PURCHASES',
      'PURCHASES_VIEW_ORDERS': 'PURCHASES',
      'PURCHASES_RETURNS': 'PURCHASES',

      // Reports submodules
      'REPORTS_LOCATION_SALES': 'REPORTS',
      'REPORTS_TOP_SELLING_PRODUCTS': 'REPORTS',
      'REPORTS_PURCHASES': 'REPORTS',
      'REPORTS_INVENTORY': 'REPORTS',
      'REPORTS_STOCK': 'REPORTS',
      'REPORTS_STOCK_MATRIX': 'REPORTS',
      'REPORTS_DAY_CLOSE': 'REPORTS',
      'REPORTS_STOCK_TRANSFER': 'REPORTS',
      'REPORTS_PRODUCTS': 'REPORTS',
      'REPORTS_BILLING': 'REPORTS',
      'REPORTS_INVOICE_PROFIT_LOSS': 'REPORTS',
      'REPORTS_CREDIT_CUSTOMERS': 'REPORTS',
      'REPORTS_GST': 'REPORTS',
      'REPORTS_GSTR2': 'REPORTS',
      'REPORTS_GSTR3B': 'REPORTS',
      'REPORTS_HSN': 'REPORTS',

      // Physical Stock Management submodules
      'PHYSICAL_STOCK_MANAGEMENT': 'INVENTORY',
      'PHYSICAL_STOCK_ENTRY': 'INVENTORY',
      'PHYSICAL_STOCK_VIEW': 'INVENTORY',

      // Expenses modules
      'EXPENSES': 'EXPENSES',
      'EXPENSES_ENTRY': 'EXPENSES',

      // Settings submodules
      'SETTINGS_COMPANY': 'SETTINGS',
      'SETTINGS_WEBSITE': 'SETTINGS',
      'SETTINGS_PRINT': 'SETTINGS'
    };

    // Process main menus and submenus - save BOTH module-level and submenu-level privileges
    // CRITICAL: Send ALL privileges (including unchecked ones) so backend can update existing entries
    // This ensures that when a user unchecks a privilege, it updates the DB entry to all false
    privileges.forEach(menu => {
      const moduleKey = menuToModule[menu.id];

      // Save main menu as module-level privilege (ALWAYS send, even if all false)
      if (moduleKey) {
        modulePrivileges.push({
          module: moduleKey,
          submenuId: null, // Module-level
          canView: menu.canView || false,
          canCreate: menu.canCreate || false,
          canEdit: menu.canEdit || false,
          canDelete: menu.canDelete || false
        });
      }

      // Process submenus - save as submenu-level privileges (ALWAYS send, even if all false)
      menu.submenus.forEach(submenu => {
        const submenuModuleKey = menuToModule[submenu.id];
        if (submenuModuleKey) {
          modulePrivileges.push({
            module: submenuModuleKey,
            submenuId: submenu.id, // Submenu-level - use the submenu ID
            canView: submenu.canView || false,
            canCreate: submenu.canCreate || false,
            canEdit: submenu.canEdit || false,
            canDelete: submenu.canDelete || false
          });
        }
      });
    });

    return {
      role: selectedRole,
      privileges: modulePrivileges
    };
  };

  const handleRoleChange = (event) => {
    setSelectedRole(event.target.value);
  };

  // Handle "Select All" checkbox - toggles all 4 checkboxes (View, Create, Edit, Delete) for a row
  // If main menu, ALSO toggles all submenus
  const handleSelectAll = (menuIndex, submenuIndex = -1) => {
    setPrivileges(prevPrivileges => {
      const newPrivileges = [...prevPrivileges];
      const menu = newPrivileges[menuIndex];

      if (submenuIndex === -1) {
        // Main menu row - Check if EVERYTHING is selected (parent + all submenus)
        const parentFullyChecked = menu.canView && menu.canCreate && menu.canEdit && menu.canDelete;
        const allSubmenusFullyChecked = menu.submenus.length === 0 ||
          menu.submenus.every(s => s.canView && s.canCreate && s.canEdit && s.canDelete);

        const isEverythingChecked = parentFullyChecked && allSubmenusFullyChecked;
        const newValue = !isEverythingChecked;

        // Update parent
        newPrivileges[menuIndex] = {
          ...menu,
          canView: newValue,
          canCreate: newValue,
          canEdit: newValue,
          canDelete: newValue,
          submenus: menu.submenus.map(s => ({
            ...s,
            canView: newValue,
            canCreate: newValue,
            canEdit: newValue,
            canDelete: newValue
          }))
        };
      } else {
        // Submenu row only
        const submenu = menu.submenus[submenuIndex];
        const allChecked = submenu.canView && submenu.canCreate && submenu.canEdit && submenu.canDelete;
        const newValue = !allChecked;

        newPrivileges[menuIndex].submenus[submenuIndex] = {
          ...submenu,
          canView: newValue,
          canCreate: newValue,
          canEdit: newValue,
          canDelete: newValue
        };

        // If any submenu has view permission, ensure parent menu also has view permission
        if (newValue) {
          newPrivileges[menuIndex].canView = true;
        } else {
          // If submenu view is unchecked, check if any other submenu still has view permission
          const anySubMenuHasView = newPrivileges[menuIndex].submenus.some(s => s.canView);
          if (!anySubMenuHasView) {
            // newPrivileges[menuIndex].canView = false;
          }
        }
      }

      return newPrivileges;
    });
  };

  // Check if all 4 checkboxes are selected for a row
  // For main menu, checks if EVERYTHING (parent + all submenus) is selected
  const isAllSelected = (menuIndex, submenuIndex = -1) => {
    const menu = privileges[menuIndex];
    if (submenuIndex === -1) {
      // Main menu row
      const parentFullyChecked = menu.canView && menu.canCreate && menu.canEdit && menu.canDelete;
      const allSubmenusFullyChecked = menu.submenus.length === 0 ||
        menu.submenus.every(s => s.canView && s.canCreate && s.canEdit && s.canDelete);

      return parentFullyChecked && allSubmenusFullyChecked;
    } else {
      // Submenu row
      const submenu = menu.submenus[submenuIndex];
      return submenu.canView && submenu.canCreate && submenu.canEdit && submenu.canDelete;
    }
  };

  // Check if some (but not all) checkboxes are selected (for indeterminate state)
  const isIndeterminate = (menuIndex, submenuIndex = -1) => {
    const menu = privileges[menuIndex];
    if (submenuIndex === -1) {
      // Main menu row - indeterminate if NOT all selected but SOMETHING is selected
      const allSelected = isAllSelected(menuIndex);
      if (allSelected) return false;

      // Check if any checkbox is checked in parent or submenus
      const parentAnyChecked = menu.canView || menu.canCreate || menu.canEdit || menu.canDelete;
      const submenusAnyChecked = menu.submenus.some(s => s.canView || s.canCreate || s.canEdit || s.canDelete);

      return parentAnyChecked || submenusAnyChecked;
    } else {
      // Submenu row
      const submenu = menu.submenus[submenuIndex];
      const checkedCount = [submenu.canView, submenu.canCreate, submenu.canEdit, submenu.canDelete].filter(Boolean).length;
      return checkedCount > 0 && checkedCount < 4;
    }
  };

  const handlePrivilegeChange = (menuIndex, field, submenuIndex = -1) => {
    setPrivileges(prevPrivileges => {
      const newPrivileges = [...prevPrivileges];
      const menu = newPrivileges[menuIndex];

      if (submenuIndex === -1) {
        // Main menu privilege change
        const newValue = !menu[field];
        newPrivileges[menuIndex] = {
          ...menu,
          [field]: newValue
        };

        // Cascade to submenus for the same field
        if (menu.submenus.length > 0) {
          newPrivileges[menuIndex].submenus = menu.submenus.map(s => {
            const updatedSub = { ...s, [field]: newValue };

            // If we're turning off canView, turn off EVERYTHING for submenus
            if (field === 'canView' && !newValue) {
              updatedSub.canCreate = false;
              updatedSub.canEdit = false;
              updatedSub.canDelete = false;
            }
            return updatedSub;
          });
        }

        // If main menu view is unchecked, also uncheck other parent permissions for safety
        if (field === 'canView' && !newValue) {
          newPrivileges[menuIndex].canCreate = false;
          newPrivileges[menuIndex].canEdit = false;
          newPrivileges[menuIndex].canDelete = false;
        }
      } else {
        // Submenu privilege change
        const submenu = menu.submenus[submenuIndex];
        const newValue = !submenu[field];

        newPrivileges[menuIndex].submenus[submenuIndex] = {
          ...submenu,
          [field]: newValue
        };

        if (field === 'canView') {
          if (newValue) {
            // If a submenu view is checked, ensure parent menu view is checked
            newPrivileges[menuIndex].canView = true;
          } else {
            // If a submenu view is unchecked, uncheck other permissions for this submenu
            newPrivileges[menuIndex].submenus[submenuIndex].canCreate = false;
            newPrivileges[menuIndex].submenus[submenuIndex].canEdit = false;
            newPrivileges[menuIndex].submenus[submenuIndex].canDelete = false;

            // Only uncheck parent menu view if no submenu has view permission
            const anySubMenuHasView = newPrivileges[menuIndex].submenus.some(s => s.canView);
            if (!anySubMenuHasView) {
              // newPrivileges[menuIndex].canView = false;
            }
          }
        }
      }

      return newPrivileges;
    });
  };

  const handleSavePrivileges = async () => {
    if (!selectedRole) {
      enqueueSnackbar('Please select a role', { variant: 'error' });
      return;
    }

    try {
      setSaveLoading(true);

      // Save submenu-specific permissions to localStorage
      const submenuPermissions = privileges.flatMap(menu =>
        menu.submenus.map(submenu => ({
          id: submenu.id,
          canView: submenu.canView,
          canCreate: submenu.canCreate,
          canEdit: submenu.canEdit,
          canDelete: submenu.canDelete
        }))
      );
      saveSubmenuPermissions(selectedRole, submenuPermissions);

      // Convert UI format to service format for main localStorage privileges
      const serviceFormat = convertUIToServiceFormat(privileges);
      console.log('Saving privileges for role:', selectedRole, serviceFormat);

      // Save using the privilege service for frontend localStorage
      const success = saveRolePrivileges(selectedRole, serviceFormat);

      // Always save to backend as well
      try {
        const backendFormat = convertUIToBackendFormat(privileges);
        console.log('Saving backend privileges:', backendFormat);

        await axiosInstance.put('/role-privileges/update', backendFormat);
        enqueueSnackbar('Role privileges saved successfully', { variant: 'success' });
      } catch (error) {
        console.error('Failed to save backend privileges:', error);
        console.error('Error details:', error.response?.data);
        enqueueSnackbar('Role privileges saved in frontend but failed to save in backend', { variant: 'warning' });
      }

      setSaveLoading(false);
    } catch (error) {
      console.error('Error saving role privileges:', error);
      enqueueSnackbar('Failed to save role privileges', { variant: 'error' });
      setSaveLoading(false);
    }
  };

  const handleRefresh = () => {
    if (selectedRole) {
      fetchRolePrivileges(selectedRole);
    }
  };

  const getUserRoleLabel = (role) => {
    switch (role) {
      case 'ADMIN':
        return 'Admin';
      case 'MANAGER':
        return 'Manager';
      case 'EXECUTIVE':
        return 'Executive';
      case 'BILLING_PERSON':
        return 'Billing Person';
      case 'USER':
        return 'Regular User';
      default:
        return role;
    }
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Role-Based Privileges Management
        </Typography>

        

        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Select Role</InputLabel>
                <Select
                  value={selectedRole}
                  onChange={handleRoleChange}
                  label="Select Role"
                  disabled={loading}
                >
                  <MenuItem value="" disabled>
                    -- Select a role --
                  </MenuItem>
                  {roles.filter(role => role !== 'USER').map((role) => (
                    <MenuItem key={role} value={role}>
                      {getUserRoleLabel(role)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                sx={{ mr: 1 }}
                disabled={!selectedRole || loading}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSavePrivileges}
                disabled={!selectedRole || loading || saveLoading}
              >
                {saveLoading ? <CircularProgress size={24} /> : 'Save Privileges'}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {selectedRole && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Privileges for {getUserRoleLabel(selectedRole)}
            </Typography>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer component={Paper} sx={{ mb: 3 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Menu</TableCell>
                        <TableCell align="center">Select All</TableCell>
                        <TableCell align="center">View</TableCell>
                        <TableCell align="center">Create</TableCell>
                        <TableCell align="center">Edit</TableCell>
                        <TableCell align="center">Delete</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {privileges.map((menu, menuIndex) => (
                        <React.Fragment key={menu.id}>
                          <TableRow
                            sx={{
                              '& > *': { fontWeight: 'bold' },
                              backgroundColor: '#f5f5f5'
                            }}
                          >
                            <TableCell>
                              {menu.text}
                            </TableCell>
                            <TableCell align="center">
                              <Checkbox
                                checked={isAllSelected(menuIndex)}
                                indeterminate={isIndeterminate(menuIndex)}
                                onChange={() => handleSelectAll(menuIndex)}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Checkbox
                                checked={menu.canView}
                                onChange={() => handlePrivilegeChange(menuIndex, 'canView')}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Checkbox
                                checked={menu.canCreate}
                                onChange={() => handlePrivilegeChange(menuIndex, 'canCreate')}
                                disabled={!menu.canView}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Checkbox
                                checked={menu.canEdit}
                                onChange={() => handlePrivilegeChange(menuIndex, 'canEdit')}
                                disabled={!menu.canView}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Checkbox
                                checked={menu.canDelete}
                                onChange={() => handlePrivilegeChange(menuIndex, 'canDelete')}
                                disabled={!menu.canView}
                              />
                            </TableCell>
                          </TableRow>

                          {/* Submenus */}
                          {menu.submenus.length > 0 && menu.submenus.map((submenu, submenuIndex) => (
                            <TableRow key={submenu.id}>
                              <TableCell sx={{ pl: 4 }}>
                                {submenu.text}
                              </TableCell>
                              <TableCell align="center">
                                <Checkbox
                                  checked={isAllSelected(menuIndex, submenuIndex)}
                                  indeterminate={isIndeterminate(menuIndex, submenuIndex)}
                                  onChange={() => handleSelectAll(menuIndex, submenuIndex)}
                                  disabled={!menu.canView}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Checkbox
                                  checked={submenu.canView}
                                  onChange={() => handlePrivilegeChange(menuIndex, 'canView', submenuIndex)}
                                  disabled={!menu.canView}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Checkbox
                                  checked={submenu.canCreate}
                                  onChange={() => handlePrivilegeChange(menuIndex, 'canCreate', submenuIndex)}
                                  disabled={!submenu.canView}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Checkbox
                                  checked={submenu.canEdit}
                                  onChange={() => handlePrivilegeChange(menuIndex, 'canEdit', submenuIndex)}
                                  disabled={!submenu.canView}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Checkbox
                                  checked={submenu.canDelete}
                                  onChange={() => handlePrivilegeChange(menuIndex, 'canDelete', submenuIndex)}
                                  disabled={!submenu.canView}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {backendPrivileges && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>Backend Privileges Status</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="subtitle2" gutterBottom>
                        Backend privileges for role: {backendPrivileges.role}
                      </Typography>
                      <TableContainer component={Paper}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Module</TableCell>
                              <TableCell align="center">View</TableCell>
                              <TableCell align="center">Create</TableCell>
                              <TableCell align="center">Edit</TableCell>
                              <TableCell align="center">Delete</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {backendPrivileges.privileges.map((priv) => (
                              <TableRow key={priv.module}>
                                <TableCell>{priv.module}</TableCell>
                                <TableCell align="center">
                                  <Checkbox
                                    checked={priv.canView}
                                    disabled={true}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Checkbox
                                    checked={priv.canCreate}
                                    disabled={true}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Checkbox
                                    checked={priv.canEdit}
                                    disabled={true}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Checkbox
                                    checked={priv.canDelete}
                                    disabled={true}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <Box sx={{ mt: 2 }}>
                        <Alert severity="info">
                          This section shows the current privileges stored in the database. The checkboxes above show what you're configuring to save.
                        </Alert>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                )}
              </>
            )}

            {/* Save Button at Bottom */}
            {selectedRole && !loading && (
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleRefresh}
                  disabled={!selectedRole || loading}
                >
                  Refresh
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSavePrivileges}
                  disabled={!selectedRole || loading || saveLoading}
                  size="large"
                >
                  {saveLoading ? <CircularProgress size={24} /> : 'Save Privileges'}
                </Button>
              </Box>
            )}
          </Paper>
        )}
      </Box>
    </Layout>
  );
};

export default RolePrivilegesPage; 